/**
 * Centralized Error Messages
 * Maps error codes to user-friendly messages
 */

export const ERROR_MESSAGES = {
  // Authentication Errors
  AUTH_001: 'Your session has expired. Please login again.',
  AUTH_002: 'Invalid credentials. Please check your email and password.',
  AUTH_003: 'Your account is locked. Please contact support.',
  AUTH_004: 'Email already exists. Please use a different email.',
  AUTH_005: 'Password must be at least 8 characters long.',

  // Document Errors
  DOC_001: 'Document not found. It may have been deleted.',
  DOC_002: 'You don\'t have permission to access this document.',
  DOC_003: 'Failed to upload document. Please try again.',
  DOC_004: 'Document format not supported. Accepted: PDF, JPG, PNG.',
  DOC_005: 'File size exceeds limit (max 10MB).',
  DOC_006: 'Document title is required.',
  DOC_007: 'Failed to delete document. Please try again.',
  DOC_008: 'Failed to process document. Please try again.',

  // Processing Errors
  PROC_001: 'OCR processing failed. Please try uploading again.',
  PROC_002: 'Document is being processed. Please wait.',
  PROC_003: 'No OCR provider configured. Please add API key in Settings.',
  PROC_004: 'OCR API key is invalid or expired.',
  PROC_005: 'OCR processing timed out. Please try again.',
  PROC_006: 'Failed to extract pages from document.',

  // Page Errors
  PAGE_001: 'Page not found.',
  PAGE_002: 'Failed to save crop settings.',
  PAGE_003: 'Failed to save transcription.',
  PAGE_004: 'Failed to save translation.',
  PAGE_005: 'Failed to load page data.',

  // Network Errors
  NET_001: 'Connection failed. Check your internet connection.',
  NET_002: 'Server is temporarily unavailable. Try again later.',
  NET_003: 'Request timed out. Please try again.',
  NET_004: 'Too many requests. Please wait a moment.',

  // Validation Errors
  VAL_001: 'Please fill in all required fields.',
  VAL_002: 'Invalid input format.',
  VAL_003: 'Email format is invalid.',
  VAL_004: 'Field exceeds maximum length.',
  VAL_005: 'Field is below minimum length.',

  // Document Type Errors
  DOCTYPE_001: 'Document type not found.',
  DOCTYPE_002: 'Failed to create document type.',
  DOCTYPE_003: 'Failed to update document type.',
  DOCTYPE_004: 'Failed to delete document type.',
  DOCTYPE_005: 'Document type name is required.',

  // API Key Errors
  APIKEY_001: 'API key is required.',
  APIKEY_002: 'Invalid API key format.',
  APIKEY_003: 'Failed to save API key.',
  APIKEY_004: 'Failed to delete API key.',

  // Default
  DEFAULT: 'Something went wrong. Please try again or contact support.'
};

/**
 * Get user-friendly error message from error object
 * @param {Error|Object} error - Error object from API or network
 * @returns {Object} - { message: string, code: string|null }
 */
export const getErrorMessage = (error) => {
  // Handle network errors
  if (!error.response) {
    return {
      message: ERROR_MESSAGES.NET_001,
      code: 'NET_001'
    };
  }

  // Extract error code from backend response
  const errorCode = error?.response?.data?.code || error?.code;

  // Get user-friendly message
  const message = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.DEFAULT;

  // Log detailed error for debugging (only in development)
  if (import.meta.env.MODE === 'development') {
    console.error('[Error Debug]', {
      code: errorCode,
      message,
      originalError: error,
      response: error?.response?.data
    });
  }

  return { message, code: errorCode };
};

/**
 * Get error message for HTTP status codes
 * @param {number} status - HTTP status code
 * @returns {string} - User-friendly message
 */
export const getStatusMessage = (status) => {
  const statusMessages = {
    400: 'Invalid request. Please check your input.',
    401: ERROR_MESSAGES.AUTH_001,
    403: ERROR_MESSAGES.DOC_002,
    404: ERROR_MESSAGES.DOC_001,
    409: 'A conflict occurred. The resource may already exist.',
    422: ERROR_MESSAGES.VAL_001,
    429: ERROR_MESSAGES.NET_004,
    500: ERROR_MESSAGES.NET_002,
    502: ERROR_MESSAGES.NET_002,
    503: ERROR_MESSAGES.NET_002,
    504: ERROR_MESSAGES.NET_003
  };

  return statusMessages[status] || ERROR_MESSAGES.DEFAULT;
};

/**
 * Check if error is authentication related
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code || error?.code;
  return status === 401 || (code && code.startsWith('AUTH_'));
};

/**
 * Check if error is network related
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
};
